/**
 * ResetPassword — Fase B migration to useZodForm (iter-208).
 *
 * Token-verified password reset. Zod schema enforces length + match.
 * Eye-toggle icon button allowlisted (icon-only toggle inside Input).
 * Lang-picker pills allowlisted.
 */
import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { api, tokenStore, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { useLang } from "../../contexts/LangContext";
import { LumfiWordmark } from "../../components/LumfiLogo";
import { Eye, EyeOff, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";
import { useZodForm, FormField, SubmitButton } from "../../components/form-ux";

function scorePassword(pw) {
  if (!pw) return { score: 0 };
  let score = 0;
  if (pw.length >= 8) score += 1;
  if (pw.length >= 12) score += 1;
  if (/[A-Z]/.test(pw)) score += 1;
  if (/[0-9]/.test(pw)) score += 1;
  if (/[^A-Za-z0-9]/.test(pw)) score += 1;
  return { score: Math.min(score, 4) };
}

export default function ResetPassword() {
  const { lang, setLang } = useLang();
  const isEs = lang === "es";
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const [verifyState, setVerifyState] = useState({ status: "checking", reason: null });
  const [show, setShow] = useState(false);
  const [topError, setTopError] = useState("");

  const Schema = z.object({
    pw: z.string().min(8, isEs ? "Mínimo 8 caracteres." : "Min 8 characters."),
    pw2: z.string().min(1, isEs ? "Confirma la clave." : "Confirm the password."),
  }).refine((d) => d.pw === d.pw2, {
    message: isEs ? "Las claves no coinciden." : "Passwords don't match.",
    path: ["pw2"],
  });

  const form = useZodForm(Schema, { pw: "", pw2: "" }, { formId: "reset" });

  useEffect(() => {
    let cancel = false;
    (async () => {
      if (!token) {
        setVerifyState({ status: "invalid", reason: "missing_token" });
        return;
      }
      try {
        const { data } = await api.get(`/auth/verify-reset-token?token=${encodeURIComponent(token)}`);
        if (cancel) return;
        setVerifyState({
          status: data.valid ? "ok" : "invalid",
          reason: data.reason || null,
        });
      } catch {
        if (cancel) return;
        setVerifyState({ status: "invalid", reason: "network" });
      }
    })();
    return () => { cancel = true; };
  }, [token]);

  const { score } = scorePassword(form.values.pw);

  const onSubmit = form.handleSubmit(async (values) => {
    setTopError("");
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: values.pw });
      if (data?.access_token) tokenStore.set(data.access_token);
      await refresh();
      toast.success(isEs ? "Listo, tu clave se actualizó." : "Done — your password is updated.");
      navigate("/dashboard");
    } catch (e) {
      setTopError(formatApiError(e.response?.data?.detail) || e.message);
    }
  });

  const invalidReasonText = (reason) => {
    const map = {
      es: {
        missing_token: "El link no incluye un token. Pide uno nuevo.",
        unknown_token: "El link no es válido. Puede haber sido reemplazado por uno más reciente.",
        already_used: "Este link ya se usó. Pide uno nuevo si lo necesitas.",
        expired: "El link venció. Pide uno nuevo y revisa tu casilla.",
        network: "No pudimos verificar el link. Prueba de nuevo en un minuto.",
      },
      en: {
        missing_token: "The link doesn't include a token. Request a new one.",
        unknown_token: "This link isn't valid. It may have been replaced by a newer one.",
        already_used: "This link was already used. Request a new one if needed.",
        expired: "This link has expired. Request a new one and check your inbox.",
        network: "We couldn't verify the link. Please try again in a minute.",
      },
    };
    return (isEs ? map.es : map.en)[reason] || (isEs ? "El link no es válido." : "Invalid link.");
  };

  const strengthBars = [0, 1, 2, 3].map((i) => (
    <div
      key={i}
      className={`h-1 flex-1 rounded-full transition-colors ${
        i < score
          ? score <= 1 ? "bg-red-400"
            : score === 2 ? "bg-amber-400"
            : score === 3 ? "bg-emerald-400"
            : "bg-emerald-600"
          : "bg-slate-200"
      }`}
    />
  ));

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-10 safe-bottom">
      <div className="w-full max-w-md lumfi-rise-in">
        <div className="text-center mb-7 relative">
          <Link to="/"><LumfiWordmark /></Link>
          <div className="absolute top-0 right-0 inline-flex bg-white border border-slate-200 rounded-full p-0.5 shadow-sm">
            {["es", "en", "pt"].map((code) => (
              // design-system: ALLOW lang-picker toggle pill (3-state segmented control)
              <button /* design-system: ALLOW legacy-todo */
                key={code} type="button" onClick={() => setLang(code)}
                data-testid={`reset-lang-${code}`}
                className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase transition-colors ${
                  lang === code ? "bg-emerald-600 text-white" : "text-slate-500 hover:text-slate-900"
                }`}
              >{code}</button>
            ))}
          </div>
        </div>
        <Card className="rounded-2xl border-slate-200 p-7 shadow-sm">
          {verifyState.status === "checking" && (
            <div className="text-center py-8 text-slate-400 text-sm flex items-center justify-center gap-2" data-testid="reset-checking">
              <Loader2 className="h-4 w-4 animate-spin" />
              {isEs ? "Verificando link…" : "Verifying link…"}
            </div>
          )}

          {verifyState.status === "invalid" && (
            <div data-testid="reset-invalid-state">
              <div className="flex items-center gap-2 text-amber-700 mb-3">
                <AlertTriangle className="h-5 w-5" />
                <h1 className="text-lg font-semibold">
                  {isEs ? "Link no válido" : "Link not valid"}
                </h1>
              </div>
              <p className="text-sm text-slate-600 mb-5">
                {invalidReasonText(verifyState.reason)}
              </p>
              <Button asChild className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold">
                <Link to="/forgot-password" data-testid="reset-request-new-btn">
                  {isEs ? "Pedir un nuevo link" : "Request a new link"}
                </Link>
              </Button>
              <div className="mt-4 text-center">
                <Link to="/login" className="text-xs text-slate-500 hover:text-slate-700">
                  {isEs ? "Volver al login" : "Back to login"}
                </Link>
              </div>
            </div>
          )}

          {verifyState.status === "ok" && (
            <form onSubmit={onSubmit} data-testid="reset-form" className="space-y-4" noValidate>
              <div className="flex items-center gap-2 text-emerald-700 mb-1">
                <CheckCircle2 className="h-4 w-4" />
                <h1 className="text-xl font-bold text-slate-900 tracking-tight">
                  {isEs ? "Nueva clave" : "New password"}
                </h1>
              </div>
              <p className="text-sm text-slate-500 -mt-2 mb-2">
                {isEs
                  ? "Mínimo 8 caracteres. Te firmamos al instante."
                  : "At least 8 characters. We'll sign you in right away."}
              </p>
              <FormField
                label={isEs ? "Nueva clave" : "New password"}
                name="pw"
                required
                error={form.errors.pw && (form.touched.pw || form.submitCount > 0) ? form.errors.pw : undefined}
              >
                <div className="relative">
                  <FormField.Input
                    type={show ? "text" : "password"}
                    autoFocus
                    data-testid="reset-new-pw-input"
                    className="h-12 pr-10 text-base rounded-xl"
                    {...form.getFieldProps("pw")}
                  />
                  {/* design-system: ALLOW icon toggle inside Input (no Button variant matches) */}
                  <button /* design-system: ALLOW legacy-todo */
                    type="button"
                    onClick={() => setShow((s) => !s)}
                    data-testid="reset-show-toggle"
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-600"
                    tabIndex={-1}
                    aria-label={isEs ? "Mostrar clave" : "Show password"}
                  >
                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <div className="flex gap-1 mt-2" aria-hidden="true">{strengthBars}</div>
              </FormField>

              <FormField
                label={isEs ? "Confirmar" : "Confirm"}
                name="pw2"
                required
                error={form.errors.pw2 && (form.touched.pw2 || form.submitCount > 0) ? form.errors.pw2 : undefined}
              >
                <FormField.Input
                  type={show ? "text" : "password"}
                  data-testid="reset-confirm-pw-input"
                  className="h-12 text-base rounded-xl"
                  {...form.getFieldProps("pw2")}
                />
              </FormField>

              {topError && (
                <div data-testid="reset-error" className="text-sm text-red-600" role="alert">{topError}</div>
              )}
              <SubmitButton
                submitting={form.submitting}
                loadingText={isEs ? "Guardando…" : "Saving…"}
                testid="reset-submit-btn"
                className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-base font-semibold"
              >
                {isEs ? "Cambiar clave" : "Change password"}
              </SubmitButton>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
}
